import React, { useState, useEffect, useCallback } from 'react';
import {
  KeyRound, Clock, Users, MessageSquare, Plus, Settings, AlertTriangle, CheckCircle2,
  Unlock,
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { formatDateTime } from '../../utils/datetime';
import gateSystemsService from '../../services/gateSystemsService';
import GateSystemConfigDrawer from '../../components/GateSystemConfigDrawer';
import RemoteOpenerCard from '../../components/RemoteOpenerCard';
import { useFacility } from '../../context/FacilityContext';

/**
 * GateTab — gate / access control surface inside Automation (Settings).
 *
 * Sections:
 *   1. Cloud Controllers — vendor adapters configured at this facility.
 *      PTI Falcon XT shows "Bridge: Connected" when the local agent is
 *      reachable; other adapters appear when registered server-side.
 *   2. Access Schedule  — facility hours (read-only display; edited via
 *      Facility Settings).
 *   3. Access Groups    — Renters / 24-7 / Staff groupings with member
 *      counts and [Manage] CTAs.
 *   4. Text-to-Open     — feature toggle (off by default).
 *
 * Backend wiring is partial — sections without a live endpoint render a
 * "Coming soon" pill and a placeholder body so the layout is testable now.
 */

const ComingSoonPill = () => (
  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200">
    Coming soon
  </span>
);

const SectionShell = ({ icon: Icon, title, subtitle, comingSoon, actions, children }) => (
  <section className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
    <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-500" />
        <div>
          <h3 className="text-[13px] font-semibold text-slate-900 flex items-center gap-1.5">
            {title}
            {comingSoon && <ComingSoonPill />}
          </h3>
          {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {actions}
    </header>
    <div className="p-4">{children}</div>
  </section>
);


// ============================================================================
// Cloud Controllers
// ============================================================================
const CloudControllers = ({ facility, facilityId, onChange }) => {
  const [openConfig, setOpenConfig] = useState(false);
  const [firing, setFiring] = useState(false);
  const [fireResult, setFireResult] = useState(null);
  const timeZone = facility?.timezone || undefined;
  const gs = facility?.gate_system;
  const provider = gs?.provider;
  const isPTI = provider === 'pti_falcon_xt' || provider === 'pti' || provider === 'storlogix';
  const bridgeOnline = !!(gs?.last_healthcheck_status === 'ok' || gs?.bridge_status === 'connected');
  // Only providers the BACKEND registry can construct actually work
  // (backend/integrations/gate_systems/registry.py). Anything else raises on
  // move-in provisioning — a facility set to an unimplemented provider got no
  // gate code, no move-in email, and no operator task. Show that here rather
  // than rendering an unimplemented vendor as a healthy configured state.
  const IMPLEMENTED_PROVIDERS = ['manual', 'nimbio'];
  const providerWorks = IMPLEMENTED_PROVIDERS.includes(provider);
  // Providers whose adapter exposes open_gate. The backend refuses the rest
  // with a 400 rather than pretending, so this only hides a dead button.
  const REMOTE_OPEN_PROVIDERS = ['nimbio'];
  const canOpenRemotely = REMOTE_OPEN_PROVIDERS.includes(provider);

  const fireOpen = async () => {
    if (!window.confirm(
      'Open the gate now?\n\nIf the relay is already landed on the operator, '
      + 'the gate will move — make sure the area is clear.')) return;
    setFiring(true);
    setFireResult(null);
    try {
      const { data } = await gateSystemsService.openGate(facilityId);
      setFireResult({
        ok: true,
        msg: `Relay fired ${formatDateTime(data.opened_at, timeZone)}`,
      });
      onChange && onChange();
    } catch (e) {
      // Show what the provider actually said, not a generic failure.
      setFireResult({
        ok: false,
        msg: e?.response?.data?.detail || 'Could not open the gate.',
      });
    } finally {
      setFiring(false);
    }
  };

  return (
    <SectionShell
      icon={KeyRound}
      title="Cloud Controllers"
      subtitle="Vendor adapters that mint and revoke gate codes for this facility."
      actions={
        <button
          onClick={() => setOpenConfig(true)}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-blue-700 hover:bg-blue-50 rounded"
          type="button"
        >
          <Plus className="h-3 w-3" /> Add controller
        </button>
      }
    >
      {provider ? (
        <>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-medium text-slate-900">
                {isPTI ? 'PTI Falcon XT' : provider}
              </span>
              {!providerWorks && (
                <span className="px-2 py-0.5 rounded text-[10px] font-medium ring-1 bg-red-50 text-red-700 ring-red-200">
                  Not supported — codes will not provision
                </span>
              )}
              {isPTI && (
                <span className={`px-2 py-0.5 rounded text-[10px] font-medium ring-1 ${
                  bridgeOnline
                    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                    : 'bg-slate-50 text-slate-600 ring-slate-200'
                }`}>
                  Bridge: {bridgeOnline ? 'Connected' : 'Unknown'}
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {gs.code_length}-digit {gs.code_strategy} code
              {gs.last_healthcheck_at && (
                <> &middot; last check {formatDateTime(gs.last_healthcheck_at, timeZone)}</>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canOpenRemotely && (
              <button
                onClick={fireOpen}
                disabled={firing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-[12px] font-medium"
                type="button"
                data-testid="gate-open-now"
              >
                <Unlock className="h-3.5 w-3.5" />
                {firing ? 'Opening…' : 'Open gate'}
              </button>
            )}
            <button
              onClick={() => setOpenConfig(true)}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-100 rounded"
              type="button"
            >
              <Settings className="h-3 w-3" /> Configure
            </button>
          </div>
        </div>
        {fireResult && (
          <p
            className={`mt-2 text-[11px] ${fireResult.ok ? 'text-emerald-700' : 'text-red-700'}`}
            data-testid="gate-open-result"
          >
            {fireResult.msg}
          </p>
        )}
        </>
      ) : (
        <div className="text-center py-6">
          <KeyRound className="h-6 w-6 text-slate-300 mx-auto mb-2" />
          <p className="text-[12px] text-slate-500 mb-3">No controller configured for this facility.</p>
          <button
            onClick={() => setOpenConfig(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-medium"
            type="button"
          >
            <Plus className="h-3.5 w-3.5" /> Add controller
          </button>
        </div>
      )}

      {openConfig && facilityId && (
        <GateSystemConfigDrawer
          facilityId={facilityId}
          facilityName={facility?.name}
          onClose={() => setOpenConfig(false)}
          onSaved={() => { setOpenConfig(false); onChange && onChange(); }}
        />
      )}
    </SectionShell>
  );
};


// ============================================================================
// Access Schedule (read-only summary)
// ============================================================================
const AccessSchedule = ({ facility }) => {
  const gs = facility?.gate_system;
  const hours = gs?.access_hours;
  const mode = hours?.mode || '24h';
  return (
    <SectionShell
      icon={Clock}
      title="Access Schedule"
      subtitle="Facility access window. Edit in Facility Settings."
    >
      <div className="text-[13px] text-slate-700">
        {mode === '24h' ? (
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            24-hour access enabled
          </span>
        ) : (
          <span className="font-mono">
            {hours?.start || '06:00'} &mdash; {hours?.end || '22:00'}
          </span>
        )}
      </div>
      {hours?.timezone && (
        <p className="text-[11px] text-slate-500 mt-1">Timezone: {hours.timezone}</p>
      )}
    </SectionShell>
  );
};


// ============================================================================
// Access Groups
// ============================================================================
const AccessGroups = ({ facilityId }) => {
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/facilities/${facilityId}/access-groups`);
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // Endpoint likely not wired yet — fall back to a sensible default set.
      setGroups(null);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const DEFAULT_GROUPS = [
    { name: 'Renters', description: 'Active tenants in good standing', members: '—', is_default: true },
    { name: '24/7 Access', description: 'Tenants with after-hours access', members: '—' },
    { name: 'Staff', description: 'Facility staff and managers', members: '—' },
  ];

  const displayed = groups && groups.length ? groups : DEFAULT_GROUPS;
  const isPlaceholder = !groups || groups.length === 0;

  return (
    <SectionShell
      icon={Users}
      title="Access Groups"
      subtitle="Buckets that define who can enter and when."
      comingSoon={isPlaceholder}
      actions={
        !isPlaceholder ? (
          <button className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-blue-700 hover:bg-blue-50 rounded" type="button">
            <Plus className="h-3 w-3" /> New group
          </button>
        ) : null
      }
    >
      {loading ? (
        <p className="text-[12px] text-slate-500">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {displayed.map((g) => (
            <div
              key={g.name}
              className="rounded-lg border border-slate-200 p-3 hover:border-slate-300 transition-colors"
            >
              <div className="flex items-start justify-between mb-1">
                <p className="text-[13px] font-medium text-slate-900">{g.name}</p>
                {g.is_default && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">default</span>
                )}
              </div>
              {g.description && (
                <p className="text-[11px] text-slate-500 mb-2">{g.description}</p>
              )}
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-slate-500">
                  {typeof g.members === 'number' ? `${g.members} member${g.members === 1 ? '' : 's'}` : g.members}
                </span>
                <button
                  className="text-[11px] text-blue-600 hover:text-blue-700 font-medium disabled:text-slate-400 disabled:cursor-not-allowed"
                  disabled={isPlaceholder}
                  type="button"
                >
                  Manage
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionShell>
  );
};


// ============================================================================
// Text-to-Open
// ============================================================================
const TextToOpen = ({ facility }) => {
  const enabled = !!facility?.text_to_open_enabled;
  return (
    <SectionShell
      icon={MessageSquare}
      title="Text-to-Open"
      subtitle="Let tenants open the gate by texting the facility number."
      comingSoon={!enabled}
    >
      <div className="flex items-center justify-between">
        <div className="text-[13px] text-slate-700">
          Status:{' '}
          {enabled ? (
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" /> Enabled
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-slate-500">
              <AlertTriangle className="h-3.5 w-3.5" /> Disabled
            </span>
          )}
        </div>
        <button
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-100 rounded disabled:text-slate-400 disabled:cursor-not-allowed"
          disabled
          type="button"
        >
          <Settings className="h-3 w-3" /> Configure
        </button>
      </div>
    </SectionShell>
  );
};


// ============================================================================
// GateTab
// ============================================================================
const GateTab = ({ facilityId: facilityIdProp }) => {
  // Falls back to context: this renders BOTH embedded (prop supplied) and as
  // its own /settings/gate route (no props). Without the fallback the route
  // version had no facility id, load() returned early, and the screen sat on
  // "Loading gate configuration..." forever.
  const { currentFacility } = useFacility();
  const facilityId = facilityIdProp
    || currentFacility?._id || currentFacility?.id || '';

  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    // No facility yet (context still resolving) — stop showing a spinner that
    // would never clear.
    if (!facilityId) { setLoading(false); return; }
    setLoading(true);
    try {
      // Pull the facility doc + nested gate_system config; the facilities
      // endpoint embeds gate_system on the document.
      const res = await apiClient.get(`/api/facilities/${facilityId}`);
      setFacility(res.data || null);
    } catch (e) {
      // Best-effort: also try the dedicated config endpoint to surface what
      // we can if /api/facilities/{id} isn't returning gate_system.
      try {
        const cfg = await gateSystemsService.getConfig(facilityId);
        setFacility({ gate_system: cfg.data });
      } catch (e2) {
        setFacility(null);
      }
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-[13px] text-slate-500">Loading gate configuration…</p>;
  if (!facility) return (
    <p className="text-[13px] text-slate-500">
      Could not load gate configuration for this facility.
    </p>
  );

  return (
    <div className="space-y-4">
      <p className="text-[13px] text-slate-500 max-w-3xl">
        Configure how tenants enter the facility — vendor controllers, access hours,
        groups, and text-to-open.
      </p>

      <CloudControllers facility={facility} facilityId={facilityId} onChange={load} />
      <RemoteOpenerCard facilityId={facilityId} timeZone={facility?.timezone || undefined} />
      <AccessSchedule facility={facility} />
      <AccessGroups facilityId={facilityId} />
      <TextToOpen facility={facility} />
    </div>
  );
};

export default GateTab;
